import { Icon } from "../icons/Icon";
import type { NavDomain, NavMode, NavPage } from "../../config/roleMenus";

/**
 * 移动端「更多」抽屉内容。
 *
 * 与桌面侧栏的分工：桌面侧栏是**常驻**的当前域页面列表；这里是抽屉，需要同时承担：
 *   · domains 形态 → 顶部域切换（窄屏顶栏的域页签很挤，抽屉里给一份更舒服的切换入口）+ 当前域页面；
 *   · tabs 形态 → 直接列出该角色全部可见页面（这种角色没有侧栏）。
 * 复用 `.sider-item` 视觉类，保证与桌面侧栏观感一致。
 */
export interface MobileNavPanelProps {
  mode: NavMode;
  /** tabs 形态：该角色全部可见页面 */
  pages: NavPage[];
  /** domains 形态：可见业务域（含域内页面） */
  domains: NavDomain[];
  activeDomainKey: string;
  activeKey: string;
  externalPage?: NavPage;
  /** 页面 key → 角标数量（与桌面同口径，如「审批工作台」的待我审批数） */
  pageBadges?: Record<string, number>;
  onNavigate: (key: string) => void;
  onSelectDomain: (key: string) => void;
  onOpenExternal: (url: string) => void;
}

export default function MobileNavPanel({
  mode,
  pages,
  domains,
  activeDomainKey,
  activeKey,
  externalPage,
  pageBadges,
  onNavigate,
  onSelectDomain,
  onOpenExternal,
}: MobileNavPanelProps) {
  const activeDomain = domains.find((domain) => domain.key === activeDomainKey) ?? domains[0] ?? null;
  const listed = mode === "tabs" ? pages : (activeDomain?.pages ?? []);

  return (
    <div className="mobile-nav">
      {mode === "domains" ? (
        <div className="mobile-nav-domains" role="tablist" aria-label="业务域切换">
          {domains.map((domain) => (
            <button
              key={domain.key}
              type="button"
              role="tab"
              aria-selected={domain.key === activeDomain?.key}
              className={`mobile-nav-domain${domain.key === activeDomain?.key ? " is-active" : ""}`}
              onClick={() => onSelectDomain(domain.key)}
            >
              {domain.short || domain.label}
              <span className="mobile-nav-domain-count">{domain.pages.length}</span>
            </button>
          ))}
        </div>
      ) : null}

      <ul className="sider-list mobile-nav-list">
        {listed.map((page) => {
          const active = page.key === activeKey;
          // 角标与桌面同口径（如「审批工作台」的待我审批数），数量为 0 时不渲染
          const badge = pageBadges?.[page.key] ?? 0;
          return (
            <li key={page.key}>
              <button
                type="button"
                className={`sider-item${active ? " is-active" : ""}`}
                aria-current={active ? "page" : undefined}
                onClick={() => onNavigate(page.key)}
              >
                <span className="sider-item-icon">{page.icon}</span>
                <span className="sider-item-label">{page.label}</span>
                {badge > 0 ? <span className="sider-item-badge">{badge > 99 ? "99+" : badge}</span> : null}
              </button>
            </li>
          );
        })}
      </ul>

      {externalPage?.externalUrl ? (
        <button
          type="button"
          className="sider-item is-external mobile-nav-external"
          onClick={() => onOpenExternal(externalPage.externalUrl as string)}
        >
          <span className="sider-item-icon">{externalPage.icon}</span>
          <span className="sider-item-label">{externalPage.label}</span>
          <Icon name="link" size={12} />
        </button>
      ) : null}
    </div>
  );
}
